import { Component, OnDestroy, OnInit, ViewChild, computed, inject, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CementoCheckoutFlowService } from 'src/app/services/cemento-checkout-flow.service';
import { CementoFechaServiciosSelection } from 'src/app/models/cemento-checkout';
import { ScheduleDatepickerComponent } from 'src/app/shared/schedule-datepicker/schedule-datepicker.component';
import { CementoCotizacionService } from 'src/app/services/cemento-cotizacion.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-cemento-fecha-servicios-section',
  templateUrl: './cemento-fecha-servicios-section.component.html',
  styleUrls: ['./cemento-fecha-servicios-section.component.css'],
})
export class CementoFechaServiciosSectionComponent implements OnInit, OnDestroy {
  private readonly flow = inject(CementoCheckoutFlowService);
  private readonly quoteService = inject(CementoCotizacionService);
  private readonly checkoutState = toSignal(this.flow.checkoutState);
  private configSubscription?: Subscription;

  @ViewChild(ScheduleDatepickerComponent)
  scheduleDatepicker!: ScheduleDatepickerComponent;

  readonly validChange = output<boolean>();
  readonly cutoffHour = signal(17);
  readonly minScheduleDate = signal<string | null>(null);

  readonly leadHours = computed<number>(
    () => this.checkoutState()?.cantidadServicios?.leadHours ?? 24,
  );
  readonly initialSchedule = computed(
    () => this.checkoutState()?.fechaServicios?.schedule ?? null
  );

  ngOnInit(): void {
    this.loadConfigForCurrentLeadHours();
    this.configSubscription = this.flow.checkoutState.subscribe(() => {
      this.loadConfigForCurrentLeadHours();
    });
  }

  ngOnDestroy(): void {
    this.configSubscription?.unsubscribe();
  }

  onValidChange(valid: boolean): void {
    this.validChange.emit(valid);
  }

  resetSelection(): void {
    this.scheduleDatepicker?.resetSelection();
    this.validChange.emit(false);
  }

  getSelection(): CementoFechaServiciosSelection | null {
    const result = this.scheduleDatepicker?.getSelection();
    if (!result) {
      return null;
    }
    return { schedule: result.schedule };
  }

  private calculateLocalMinScheduleDate(
    leadHours: number,
    cutoffHour: number
  ): string {
    const now = new Date();
    const leadDays = Math.ceil(Math.max(leadHours, 0) / 24);
    const extraDay = now.getHours() >= cutoffHour ? 1 : 0;
    const requiredBusinessDays = leadDays + extraDay;
    const startDay = this.nextBusinessDay(now);
    const minDay = this.addBusinessDays(startDay, requiredBusinessDays);
    return this.toIsoDate(this.nextBusinessDay(minDay));
  }

  private resolveEffectiveMinDate(
    localMinDateIso: string,
    backendMinDateIso: string | null
  ): string {
    const localMinDate = this.parseIsoDate(localMinDateIso);
    const backendMinDate = backendMinDateIso ? this.parseIsoDate(backendMinDateIso) : null;
    if (!backendMinDate) {
      return localMinDateIso;
    }
    return backendMinDate > localMinDate
      ? this.toIsoDate(backendMinDate)
      : localMinDateIso;
  }

  private nextBusinessDay(baseDate: Date): Date {
    const current = new Date(baseDate);
    while (current.getDay() === 0 || current.getDay() === 6) {
      current.setDate(current.getDate() + 1);
    }
    return current;
  }

  private addBusinessDays(baseDate: Date, businessDays: number): Date {
    const current = new Date(baseDate);
    let remaining = Math.max(businessDays, 0);
    while (remaining > 0) {
      current.setDate(current.getDate() + 1);
      if (current.getDay() !== 0 && current.getDay() !== 6) {
        remaining -= 1;
      }
    }
    return current;
  }

  private parseIsoDate(isoDate: string): Date {
    const [year, month, day] = isoDate.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private loadConfigForCurrentLeadHours(): void {
    const leadHours = this.leadHours();
    this.quoteService.getConfig(leadHours).subscribe({
      next: (config) => {
        this.cutoffHour.set(config.cutoffHour);
        const localMinScheduleDate = this.calculateLocalMinScheduleDate(
          leadHours,
          config.cutoffHour
        );
        const backendMinScheduleDate = config.minScheduleDate ?? null;
        this.minScheduleDate.set(
          this.resolveEffectiveMinDate(localMinScheduleDate, backendMinScheduleDate)
        );
      },
      error: () => {
        this.cutoffHour.set(17);
        this.minScheduleDate.set(this.calculateLocalMinScheduleDate(leadHours, 17));
      },
    });
  }
}
